import React, { useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import AiStatusDot from '../../AiAdmin/Components/AiStatusDot';
import { stateDescriptor } from '../../../Services/AiActivityService';
import { DRAFT_CONTRACT, RESOLUTION } from '../../../Services/CustomerServiceDeskService';
import { formatDateTime } from '../../../Utils/timezone';

/**
 * THE AGENT'S SUGGESTION, IN THE CONVERSATION IT IS ABOUT — or the fact that
 * there isn't one.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ACCEPTING A DRAFT SENDS NOTHING. THERE IS NO SEND CONTROL HERE, NOT EVEN A
 * DISABLED ONE, AND THERE MUST NOT BE.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The drafting pipeline has no outbound transport on either side — the API's
 * `DraftCheckNoSend` test walks the real import graph to hold that, and there
 * is no send call in `CustomerServiceDeskService` to hold ours. Accepting
 * RECORDS `used_draft`: "this is the answer, and it is the agent's". What
 * happens to an accepted text is a later phase's question. A disabled send
 * button would be a promise about a mechanism that does not exist.
 *
 * ── THE CONTROLS ARE RESOLUTIONS, AND EVERY ONE OF THEM CLOSES THE REVIEW ──
 * This card used to post its own `approve | discard` decision, which was a
 * second spelling of the four `DRAFT_RESOLUTIONS` the server records. Accept
 * is `used_draft`, accepting an edit is `used_draft` with the text that was
 * sent beside it, and rejecting is `rejected`. All three are CLOSING
 * decisions: the server resolves the thread for this draft and refuses a
 * second resolution of it, so "discard the suggestion and leave the
 * conversation open" is not a thing this screen can honestly offer and the
 * copy below says what the button actually does.
 *
 * ── WHY IT IS HERE AND NOT ON A REVIEW SCREEN OF ITS OWN ──
 * A reviewer's question is "is this the right answer to what they asked",
 * which cannot be answered without the message above it. A queue of drafts
 * stripped of their conversations is a screen that trains people to approve.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * A RUN WITH NO DRAFT IS THE COMMON CASE AND IS SAID OUT LOUD.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * 29 of 40 runs in the production evaluation of 2026-10-26 failed on a
 * Bedrock `ServiceUnavailableException`, which records no draft at all. A
 * truncated run is NOT one of these any more — see below. A blank space
 * where a suggestion would be reads as "nothing needed saying" — the opposite
 * of the truth, and the reading that would let a throttled pipeline look like
 * a quiet one for a week. So a failed run renders as a stated failure with
 * its reason, in the timeline, where the missing suggestion would have been.
 *
 * Nothing at all renders only when there was NO RUN: no agent has looked at
 * this conversation, and silence is then the honest answer.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE DRAFT IS SHOWN VERBATIM, AND LABELLED FOR WHAT THE SERVER SAYS IT IS.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * "I am seeing reasoning that i like, but make it clear what's part of the
 * draft reply, vs what's not."
 *
 * THIS CARD USED TO HAVE ONE STATE — "the agent's whole answer", under a
 * blanket "NOT A CLEAN REPLY" warning — because the single read served no
 * `draft_contract` and the only honest claim about the text was that we had
 * not been told what it was. IT IS SERVED NOW (`LatestDraft.draft_contract`
 * and `LatestDraft.narration`), so the blanket warning has become the wrong
 * answer rather than the cautious one: it fired over replies that came
 * through the reply tool, which production says is nearly all of them — 10
 * of the 10 runs that produced a draft in the first 18 hours after
 * `submit_draft` deployed recorded `submitted`
 * (`Pickup-Hungary-prod-Ai-AutonomousDraft`, 2026-10-27).
 *
 * SO THERE ARE FOUR RENDERINGS AND THE SERVER PICKS WHICH, never this file:
 *
 *   `submitted` /    THE REPLY, ALONE. The model put it in the `submit_draft`
 *   `delimited`      argument or between `<reply>` markers and the server
 *                    kept only that. Named "the agent's reply" and shown with
 *                    NO warning, because there is nothing to warn about.
 *   `undelimited`    REPLY AND REASONING MIXED. The whole answer was stored
 *                    as the draft. This is the row the old warning was
 *                    written for and it keeps it, verbatim-and-unsplit.
 *   `truncated`      CUT OFF MID-ANSWER. Its own card (`CutOff`), because
 *                    "it produced nothing" and "it produced half a reply" are
 *                    different pieces of news and the old code said the first.
 *   absent           "WE WERE NOT TOLD" — a row written before the contract
 *                    existed, or one whose word the API did not recognise and
 *                    dropped. It keeps the `undelimited` warning, worded for
 *                    what it actually knows. Absent must never read as clean.
 *
 * WHY NO NUMBER IS QUOTED IN THE WARNING ANY MORE. It used to cite "10 of the
 * 11 drafts that landed in a 40-email evaluation", which predates both the
 * 24KB tool-result fix and the reply contract and was being read as current.
 * The row now says what it is, so the copy does not need a rate to justify
 * itself — and the rate that IS current belongs where it is measured, not in
 * a browser bundle that deploys separately. For the record, as of 2026-10-26:
 * `output_discipline` is 0.35 for the live model (Sonnet 5.5) over 100 cases
 * judged by Claude Opus 4.8, n=300, and ranges 0.23–0.66 across eight models
 * — so no upgrade fixes it, which is why the contract is a slot and not a
 * firmer sentence. See `Constants/DraftReview.ts` in the API.
 *
 * STILL NOTHING IS TRIMMED, SPLIT OR REFORMATTED. `<pre>` with
 * `white-space: pre-wrap`, and the character count kept: on an `undelimited`
 * row the rambling is the clearest signal a reviewer has about the draft's
 * quality. SPLITTING THE TEXT ON A GUESS REMAINS THE ONE FORBIDDEN MOVE —
 * a wrong split either hides the answer or hands a reviewer the agent's
 * deliberation as sendable text. The server already did the split, on its own
 * row, with the model's own markers; this card reads the verdict and does not
 * second-guess it.
 *
 * ── THE NARRATION IS SHOWN, LABELLED, AND NEVER JOINED TO THE REPLY ──
 * `draft.narration` is the prose the model wrote AROUND the reply, which the
 * server keeps in its own key. It is rendered under its own heading, below
 * the reply, as text — and it is kept rather than hidden because the owner
 * asked for it kept. TWO INVARIANTS RIDE WITH IT, both argued in the API's
 * `DraftAnswer.narration`: it is never part of what could be sent to a
 * customer, and it is UNTRUSTED MODEL OUTPUT — text a model wrote about a
 * customer's own words. It goes through JSX as a string like everything else
 * here; nothing on this card uses `dangerouslySetInnerHTML`. Two ServiceOffers
 * screens in this repo render partner email HTML raw. Do not make this the
 * third.
 *
 * ── HUMAN APPROVAL IS A PHASE, AND THIS RENDERS THE PHASE AFTER IT TOO ──
 * The end state is autonomy for some categories, so a conversation the agent
 * answered by itself has to be representable in the same timeline rather than
 * being invisible to anybody who is not an administrator. That is what
 * `auto_applied` renders: the same card, in the same place, with no controls
 * and with the fact stated — it went out with nobody looking. The states are
 * `AiDecision.state` and the words are `AiActivityService`'s, because the
 * drafting pipeline already records every run under them and a second
 * vocabulary for the same five outcomes would be a second thing to keep in
 * step.
 *
 * ── A RUN THAT CONCLUDED NOTHING NEEDED ANSWERING IS ITS OWN ANSWER ──
 * An earlier version of this file said there was no such field and read the
 * case off a draft whose whole text was `**No reply needed.**`. The server
 * parses its own sentinel and answers `draft.no_reply_needed` — the model's
 * kind, or `null` for "it said so and named no reason this codebase
 * recognises" — with `state: 'no_action'` beside it. So that run renders as
 * what it was and NOT as a failure, which is the distinction that matters:
 * "the agent could not produce a draft" and "the agent decided none was
 * needed" are opposite pieces of news. Closing the conversation stays a
 * RESOLUTION a person records, offered once in `HolderControls`.
 *
 * AND WHEN A NO-REPLY ROW STILL RENDERS AS A DRAFT HERE, THE FAULT IS NOT IN
 * THIS FILE. On 2026-10-27 the owner was shown
 * `NO_REPLY_NEEDED: nothing_to_answer` — 34 characters — as "the agent's
 * whole answer", under the draft warning, with a character count framed as
 * draft quality. That row really was a DRAFT on the wire: the agent had
 * passed the sentinel as the `submit_draft` reply argument, and the API's
 * CHECK 0 believed the slot literally and recorded `output.draft` with
 * `draft_contract: 'submitted'`. The branch below was correct and never
 * fired, because `draft.text` was a string. It is fixed where the agent's
 * answer is READ — `parseDraftAnswer` now runs the sentinel over the tool
 * argument as well as the prose — and it is named here only so the next
 * person to see this symptom looks at the row before they look at the card.
 * ROWS WRITTEN BEFORE THAT FIX KEEP THEIR RECORDED OUTCOME: nothing in the
 * browser re-interprets the agent's text, which is the API's rule and not
 * ours to break.
 *
 * ── ONLY THE HOLDER MAY ACT ──
 * The controls appear for whoever holds the conversation. An unclaimed draft
 * is readable and says to take it first, which is the same rule as everywhere
 * else on this screen and the reason the holder exists: two people acting on
 * one suggestion is the failure this is built to prevent.
 */

/**
 * A run that produced nothing, stated rather than left blank.
 *
 * The reason comes off the run — `error` is a message and never a stack — and
 * the state's own word comes from the decision-log vocabulary, so a `failed`
 * row reads the same here as it does on the Activity screen.
 */
function NoDraft({ run }) {
  const state = stateDescriptor(run.state) || { label: 'No draft', tone: 'bad' };

  return (
    <div className="cs-draft cs-draft--failed" data-testid="cs-draft-failed">
      <div className="cs-draft__head">
        <span className="cs-draft__state">
          <AiStatusDot tone={state.tone} />
          {state.label}
        </span>
        {run.occurred_at && (
          <span className="cs-draft__when">{formatDateTime(run.occurred_at)}</span>
        )}
      </div>
      <p className="cs-draft__failure mb-0">
        The agent ran and could not produce a draft. Nothing is waiting on a
        reviewer here — this conversation still needs a person to answer it.
      </p>
      {run.error && <pre className="cs-draft__error">{run.error}</pre>}
    </div>
  );
}

/**
 * THE AGENT DECIDED NOTHING NEEDED ANSWERING — not a failure, and not a draft.
 *
 * The opposite piece of news from `NoDraft`, in the same place, which is why
 * it is its own component rather than a branch inside that one. `kind` is one
 * of the four `INBOUND_INELIGIBLE_KINDS` the model may name, or `null` for "it
 * said so and gave no reason this codebase recognises" — which is recorded
 * rather than guessed at, so it is rendered as the absence it is.
 *
 * IT OFFERS NO CONTROL. A person still closes the conversation, in
 * `HolderControls`, with the resolution of that name: the agent's judgement
 * and a person's decision are two facts and the owner's comparison is a count
 * of them apart.
 */
function NoReplyNeeded({ draft }) {
  return (
    <div className="cs-draft cs-draft--no-reply" data-testid="cs-draft-no-reply">
      <div className="cs-draft__head">
        <span className="cs-draft__state">
          <AiStatusDot tone="good" />
          No reply needed
        </span>
        {draft.created_at && (
          <span className="cs-draft__when">{formatDateTime(draft.created_at)}</span>
        )}
      </div>
      <p className="cs-draft__failure mb-0">
        {draft.no_reply_needed
          ? `The agent read this one and judged that nothing in it asks for
             anything — it called it ${String(draft.no_reply_needed).replace(/_/g, ' ')}.
             Close the conversation above if you agree.`
          : `The agent read this one and judged that nothing in it asks for
             anything. It named no reason this build recognises. Close the
             conversation above if you agree.`}
      </p>
    </div>
  );
}

/**
 * THE ANSWER WAS CUT OFF — not a failure to produce one, and not a draft.
 *
 * ITS OWN CARD BECAUSE IT IS ITS OWN NEWS. Before `draft_contract` reached
 * the console these rows were invisible to the single read and this card
 * rendered `NoDraft`: "the agent ran and could not produce a draft", which is
 * the wrong half of the truth — it produced part of one and ran out of room.
 *
 * THE FRAGMENT IS NOT SHOWN, AND THE API DOES NOT EVEN SERVE IT. `output.
 * partial_draft` stays on the `ai_decisions` row for diagnosis, reachable by
 * `?run_id=` on the Activity screen; it is kept off this shape entirely so
 * that no client can render half a sentence as an answer. That rule is the
 * load-bearing one on this path — see `DRAFT_CONTRACTS` in the API — so this
 * card states what happened and offers nothing to read.
 *
 * NO CONTROLS. There is no draft to use, edit or reject, and closing the
 * conversation stays a resolution a person records in `HolderControls`.
 */
function CutOff({ draft }) {
  return (
    <div className="cs-draft cs-draft--cut-off" data-testid="cs-draft-cut-off">
      <div className="cs-draft__head">
        <span className="cs-draft__state">
          <AiStatusDot tone="bad" />
          Cut off mid-answer
        </span>
        {draft.created_at && (
          <span className="cs-draft__when">{formatDateTime(draft.created_at)}</span>
        )}
      </div>
      <p className="cs-draft__failure mb-0">
        The agent was still writing when it ran out of room, so there is no
        finished reply — it produced part of one, not nothing. The fragment is
        kept on the run record for diagnosis and is deliberately not shown
        here: half a sentence must never be mistaken for an answer. This
        conversation still needs a person to answer it.
      </p>
    </div>
  );
}

/**
 * The contract words that mean the text is the reply and nothing else.
 *
 * TWO, NOT ONE, and they are counted apart on the server for a reason this
 * screen does not share: `submitted` against `delimited` is "filled in a
 * slot" against "obeyed an instruction about shape", which is the ratio the
 * owner wants before hardening the pipeline to fail-closed. A REVIEWER'S
 * QUESTION IS NOT THAT ONE — either way the text in front of them is the
 * reply alone — so they render identically here, and the distinction stays
 * countable where it is measured. See `DRAFT_CONTRACTS` in the API.
 */
const CLEAN_CONTRACTS = [DRAFT_CONTRACT.SUBMITTED, DRAFT_CONTRACT.DELIMITED];

function DraftSuggestion({
  draft, run, canAct, busy, onDecide,
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(draft?.text || '');

  // No draft and no run: nobody has looked at this, and there is nothing to
  // say about it. The only case where this card is silent.
  if (!draft) return run ? <NoDraft run={run} /> : null;

  // TWO OUTCOMES THAT ARE NOT DRAFTS, and both reach this card as a row with
  // no reply text. They are opposite pieces of news — "nothing needed
  // answering" is a correct result, "cut off mid-answer" is a broken run — so
  // they are told apart by the fact the server recorded rather than guessed
  // at: a no-reply row carries the key, a truncated one carries the contract
  // word. Before the text branch, because neither has text to render.
  if (typeof draft.text !== 'string') {
    return draft.no_reply_needed !== undefined
      ? <NoReplyNeeded draft={draft} />
      : <CutOff draft={draft} />;
  }

  const state = stateDescriptor(draft.state) || { label: 'Drafted', tone: 'muted' };
  const pending = draft.state === 'awaiting_review' || draft.state === 'proposed';
  const autonomous = draft.state === 'auto_applied';
  const { length } = String(draft.text || '');
  // WHAT THE TEXT IS, FROM THE SERVER. An absent contract is NOT clean: it
  // means the row was written before the field existed, or carried a word the
  // API did not recognise, and either way nobody has told us where the reply
  // starts. See the header for all four renderings.
  const clean = CLEAN_CONTRACTS.includes(draft.draft_contract);

  const startEditing = () => {
    setText(draft.text || '');
    setEditing(true);
  };

  return (
    <div
      className={`cs-draft ${pending ? 'cs-draft--pending' : ''}`}
      data-testid="cs-draft"
    >
      <div className="cs-draft__head">
        <span className="cs-draft__state">
          <AiStatusDot tone={state.tone} />
          {state.label}
        </span>
        {draft.created_at && (
          <span className="cs-draft__when">{formatDateTime(draft.created_at)}</span>
        )}
      </div>

      {autonomous && (
        <p className="cs-draft__autonomous mb-0">
          The agent answered this one itself. Nobody reviewed it before it went
          out — this card is the record of that, not a request to approve.
        </p>
      )}

      {editing ? (
        <Form.Control
          as="textarea"
          rows={12}
          className="cs-draft__editor"
          value={text}
          aria-label="The draft reply"
          onChange={(event) => setText(event.target.value)}
        />
      ) : (
        <>
          {/* WHAT THE BLOCK IS, SAID BEFORE IT IS READ — and said as a label
              rather than a banner, because the owner wants the reasoning and
              a warning box over text he values would read as "hide this".
              The server says which of the two claims is true of this text;
              see the header for all four renderings. */}
          <span className="cs-draft__label">
            {clean
              ? 'The agent\u2019s reply'
              : 'The agent\u2019s whole answer \u2014 reply and reasoning together'}
          </span>
          {/* ONLY WHERE IT IS TRUE. A blanket warning over a reply that came
              through the reply channel is the wrong answer, not the cautious
              one — and it was firing over nearly every draft, which is how a
              warning stops being read at all. No rate is quoted: the row
              itself says what it is. See the header. */}
          {!clean && (
            <p className="cs-draft__unmarked" data-testid="cs-draft-unmarked">
              {'NOT A CLEAN REPLY. Nothing marks where the reasoning stops and'
               + ' the reply starts, so whatever goes out of this has to be'
               + ' picked out of it by a person. '}
              {draft.draft_contract
                ? 'The agent ignored the reply channel on this run.'
                : 'The server recorded no channel for this run, so the whole'
                  + ' answer was stored as the draft.'}
            </p>
          )}
          {/* Verbatim. The agent's own plain text, rendered as text — nothing
              on this card goes through `dangerouslySetInnerHTML`, because the
              only HTML this screen renders is the customer's and it is
              sanitised in MessageBody. */}
          <pre className="cs-draft__text">{draft.text}</pre>
          <p className="cs-draft__verbatim mb-0">
            {`${length} characters, exactly as the agent recorded them.`}
            {clean
              ? ' Nothing here is trimmed, split or reformatted \u2014 the'
                + ' server kept the reply and nothing else, and this is it.'
              : ' Nothing here is trimmed, split or reformatted \u2014 a draft'
                + ' that rambles is the clearest signal a reviewer gets about'
                + ' its quality, so the rambling is kept.'}
          </p>
          {/* THE REASONING, KEPT AND LABELLED, BELOW THE REPLY RATHER THAN
              MIXED INTO IT. "I am seeing reasoning that i like, but make it
              clear what's part of the draft reply, vs what's not." It is
              untrusted model output and it is never sendable — both
              invariants are argued in the API's `DraftAnswer.narration` —
              which is why it is a sibling block and never concatenated onto
              the text above. */}
          {draft.narration && (
            <div className="cs-draft__reasoning" data-testid="cs-draft-reasoning">
              <span className="cs-draft__label">
                {'The agent\u2019s reasoning \u2014 not part of the reply, never sent'}
              </span>
              <pre className="cs-draft__narration">{draft.narration}</pre>
            </div>
          )}
        </>
      )}

      {pending && canAct && (
        <>
          <div className="cs-draft__actions">
            {editing ? (
              <>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={busy || !text.trim()}
                  onClick={() => onDecide(RESOLUTION.USED_DRAFT, text)}
                >
                  {busy && <Spinner animation="border" size="sm" className="me-2" />}
                  Accept my edit
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={busy}
                  onClick={() => setEditing(false)}
                >
                  Cancel the edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={busy}
                  onClick={() => onDecide(RESOLUTION.USED_DRAFT)}
                >
                  {busy && <Spinner animation="border" size="sm" className="me-2" />}
                  Use this wording
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={busy}
                  onClick={startEditing}
                >
                  Edit it
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  disabled={busy}
                  onClick={() => onDecide(RESOLUTION.REJECTED)}
                >
                  Reject it
                </Button>
              </>
            )}
          </div>
          <p className="cs-draft__note mb-0">
            {'Using this wording records that the agent\u2019s draft was the'
             + ' answer. It does not send it: there is no outbound path in this'
             + ' phase. Rejecting records that the draft was unusable and that'
             + ' nothing went out. BOTH CLOSE THIS CONVERSATION for this draft'
             + ' \u2014 if you want to answer in your own words instead, close'
             + ' it with \u201cI replied\u201d above.'}
          </p>
        </>
      )}

      {pending && !canAct && (
        <p className="cs-draft__note mb-0">
          Take the conversation before deciding on this, so two people cannot
          act on one suggestion.
        </p>
      )}
    </div>
  );
}

export default DraftSuggestion;
